import { Add } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, GreenText, OrderableTableCell, Title } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getReadyForOutboundTripToDoList } from 'controllers/toDoLists/outboundTrips/index'
import { VehicleDto } from 'dtos'
import { ReadyForOutboundTripDto, ReadyForOutboundTripQueryParams } from 'dtos/toDoLists/outboundTrips'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { formatDate, useDebounce } from 'utils'

export default function ReadyForOutboundTripToDoList() {
  const layout = useLayout()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<ReadyForOutboundTripQueryParams>({ page: 0, pageSize: 10, order: 'asc', orderBy: 'readyForOutboundDate' })
  const [toDoList, setToDoList] = useState<ReadyForOutboundTripDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: ReadyForOutboundTripQueryParams) => {
    layout.setIsLayoutLoading(true)
    getReadyForOutboundTripToDoList(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => {
        layout.setIsLayoutLoading(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Title>Ready For Outbound Trip</Title>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='submittedToDOT'>Submitted To DOT</MenuItem>

                    <MenuItem value='readyForOutboundDate'>Ready For Outbound Date</MenuItem>

                    <MenuItem value='vehicleReleaseDate'>Vehicle Release Date</MenuItem>

                    <MenuItem value='bondReleaseDate'>Bond Release Date</MenuItem>

                    <MenuItem value='buyerExporterCompany'>Exporter/Buyer</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'buyerExporterCompany', 'importerStockNumber', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {['submittedToDOT', 'readyForOutboundDate', 'vehicleReleaseDate', 'bondReleaseDate'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => setFilterValueFrom(e.target.value)}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => setFilterValueTo(e.target.value)}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField ||
                      (['submittedToDOT', 'readyForOutboundDate', 'vehicleReleaseDate', 'bondReleaseDate'].includes(filterField)
                        ? !filterValueFrom || !filterValueTo
                        : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'submittedToDOT') {
                        setQueryParams({
                          ...queryParams,
                          submittedToDOTFrom: filterValueFrom,
                          submittedToDOTTo: filterValueTo
                        })
                      } else if (filterField === 'readyForOutboundDate') {
                        setQueryParams({
                          ...queryParams,
                          readyForOutboundDateFrom: filterValueFrom,
                          readyForOutboundDateTo: filterValueTo
                        })
                      } else if (filterField === 'vehicleReleaseDate') {
                        setQueryParams({
                          ...queryParams,
                          vehicleReleaseDateFrom: filterValueFrom,
                          vehicleReleaseDateTo: filterValueTo
                        })
                      } else if (filterField === 'bondReleaseDate') {
                        setQueryParams({
                          ...queryParams,
                          bondReleaseDateFrom: filterValueFrom,
                          bondReleaseDateTo: filterValueTo
                        })
                      } else {
                        setQueryParams({
                          ...queryParams,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        queryParams.submittedToDOTFrom ||
                          queryParams.submittedToDOTTo ||
                          queryParams.readyForOutboundDateFrom ||
                          queryParams.readyForOutboundDateTo ||
                          queryParams.vehicleReleaseDateFrom ||
                          queryParams.vehicleReleaseDateTo ||
                          queryParams.bondReleaseDateFrom ||
                          queryParams.bondReleaseDateTo ||
                          queryParams.buyerExporterCompany ||
                          queryParams.importerStockNumber ||
                          queryParams.vin ||
                          queryParams.year ||
                          queryParams.make ||
                          queryParams.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        submittedToDOTFrom: undefined,
                        submittedToDOTTo: undefined,
                        readyForOutboundDateFrom: undefined,
                        readyForOutboundDateTo: undefined,
                        vehicleReleaseDateFrom: undefined,
                        vehicleReleaseDateTo: undefined,
                        bondReleaseDateFrom: undefined,
                        bondReleaseDateTo: undefined,
                        buyerExporterCompany: undefined,
                        importerStockNumber: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.submittedToDOTFrom && queryParams.submittedToDOTTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Submitted To DOT from "${moment(queryParams.submittedToDOTFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.submittedToDOTTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, submittedToDOTFrom: undefined, submittedToDOTTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.readyForOutboundDateFrom && queryParams.readyForOutboundDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Ready For Outbound Date from "${moment(queryParams.readyForOutboundDateFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.readyForOutboundDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, readyForOutboundDateFrom: undefined, readyForOutboundDateTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vehicleReleaseDateFrom && queryParams.vehicleReleaseDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Release Date from "${moment(queryParams.vehicleReleaseDateFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.vehicleReleaseDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vehicleReleaseDateFrom: undefined, vehicleReleaseDateTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.bondReleaseDateFrom && queryParams.bondReleaseDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Bond Release Date from "${moment(queryParams.bondReleaseDateFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.bondReleaseDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, bondReleaseDateFrom: undefined, bondReleaseDateTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.buyerExporterCompany && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter/Buyer contains "${queryParams.buyerExporterCompany}"`}
                    onDelete={() => setQueryParams({ ...queryParams, buyerExporterCompany: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains "${queryParams.vin}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vin: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${queryParams.year}`}
                    onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${queryParams.make}"`}
                    onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${queryParams.model}"`}
                    onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='submittedToDOT'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Submitted To DOT
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='readyForOutboundDate'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Ready For Outbound Date
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='vehicleReleaseDate'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Vehicle Release Date
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='bondReleaseDate'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Bond Release Date
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='buyerExporterCompany'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Exporter/Buyer
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='importerStockNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Importer Stock #
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='vin'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              VIN
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='year'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Year
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='make'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Make
                            </OrderableTableCell>

                            <OrderableTableCell<ReadyForOutboundTripDto>
                              keyName='model'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof ReadyForOutboundTripDto | undefined}
                            >
                              Model
                            </OrderableTableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {toDoList.map((item, index) => (
                            <TableRow key={item.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.submittedToDOT && moment().diff(moment(formatDate(item.submittedToDOT)), 'days') >= 30
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight: item.submittedToDOT && moment().diff(moment(formatDate(item.submittedToDOT)), 'days') >= 30 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.submittedToDOT ? formatDate(item.submittedToDOT) : '-'}
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.readyForOutboundDate && moment().diff(moment(formatDate(item.readyForOutboundDate)), 'days') > 0
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    item.readyForOutboundDate && moment().diff(moment(formatDate(item.readyForOutboundDate)), 'days') > 0 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.readyForOutboundDate ? formatDate(item.readyForOutboundDate) : '-'}
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.vehicleReleaseDate && moment().diff(moment(formatDate(item.vehicleReleaseDate)), 'days') < 0
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    item.vehicleReleaseDate && moment().diff(moment(formatDate(item.vehicleReleaseDate)), 'days') < 0 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.vehicleReleaseDate ? formatDate(item.vehicleReleaseDate) : '-'}
                              </TableCell>

                              <TableCell>{item.bondReleaseDate ? formatDate(item.bondReleaseDate) : '-'}</TableCell>

                              <TableCell>{item.buyerExporterCompany}</TableCell>

                              <TableCell>{item.importerStockNumber}</TableCell>

                              <TableCell>{item.vin}</TableCell>

                              <TableCell>{item.year}</TableCell>

                              <TableCell>{item.make}</TableCell>

                              <TableCell>{item.model}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={queryParams.pageSize}
                              page={queryParams.page}
                              onPageChange={(_, page) => {
                                setQueryParams({
                                  ...queryParams,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setQueryParams({
                                  ...queryParams,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Ready for Outbound To-Do List:**

Vehicles appear in this list if:

   - The “Ready For Outbound” Date is equal to a past or current date.
   - The vehicle’s “Bond Release Date” on the Outbound Trip card under Vehicle Details – Conformity (tab) is equal to a past or current date.

This list is sorted by the Ready For Outbound Date, from oldest to newest.

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Submitted To DOT,
   - Ready For Outbound Date
   - Vehicle Release Date,
   - Bond Release Date.
   - Exporter/Buyer.
   - Importer Stock #,
   - VIN,
   - Year,
   - Make,
   - Model

**Filter Value:**

The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

   - Submitted To DOT: Displays Filter Date From and Filter Date To.
   - Ready For Outbound Date: Displays Filter Date From and Filter Date To.
   - Vehicle Release Date: Displays Filter Date From and Filter Date To.
   - Bond Release Date: Displays Filter Date From and Filter Date To.
   - Exporter/Buyer: Displays a free-type filter field.
   - Importer Stock # a free-type filter field.
   - VIN: Displays a free-type filter field.
   - Year: Displays a free-type filter field.
   - Make: Displays a free-type filter field.
   - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Submitted To DOT Column:**

The "Submitted To DOT" date is read from the Conformity Details card under Vehicle Details – Conformity (tab). The date will appear in red if it is 30 or more days in the past.

**Ready For Outbound Date Column:**

The "Ready For Outbound" date is read from the Conformity Details card under Vehicle Details – Conformity (tab). This date is red if it is in the past. The "Ready For Outbound" Date allows vehicles to be added to Outbound Trips before the Vehicle Release Date is reached, which is typically after a 30-day hold expires.

**Vehicle Release Date Column:**

The "Vehicle Release Date" is read from the Conformity Details card under Vehicle Details – Conformity (tab). This date will appear in red if it is a future date. It is the date the DOT-mandated vehicle hold expires (usually after 30 days).

**Bond Release Date Column:**

The "Bond Release Date" is read from the Outbound Trip card under Vehicle Details – Conformity (tab). This column is nullable. If the Bond Release Date is a past or current date, the vehicle can immediately be added to an Outbound Trip, regardless of the "Ready For Outbound Date" or "Vehicle Release Date." The "Bond Release Date" refers to the date printed on the Bond Release document, not the date the document was associated with the vehicle in VTS2.

**Exporter/Buyer Column:**

Displays the Buyer company name if one is associated with the vehicle. If no Buyer is assigned, the Exporter company name is shown instead.

**Importer Stock # Column:**

Displays the Importer Stock # for the vehicle.

**VIN Column:**

Displays vehicles VIN.

**Year Column:**

Displays the Year of the Vehicle.

**Make Column:**

Displays the Make of the Vehicle.

**Model Column:**

Displays the Model of the Vehicle.

**Add to Outbound Trip:**

To remove a vehicle from this list, the user must navigate to the "Outbound Trips" feature and add the vehicle to an Outbound Trip.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
